import { Box, Button, Flex, Text, Modal, Checkbox, TextArea } from '@fullstory/components';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { StoryAISummaryFeedback } from '@fullstory/analytics';

type FeedbackDetails = Pick<
  StoryAISummaryFeedback,
  | 'tooLongOrGeneral'
  | 'inaccurateInfo'
  | 'missedKeyPoints'
  | 'missedEntireSession'
  | 'other'
  | 'additionalFeedback'
>;

interface FeedbackOption {
  key: keyof FeedbackDetails;
  label: string;
}

const FEEDBACK_OPTIONS: FeedbackOption[] = [
  {
    key: 'tooLongOrGeneral',
    label: intl.formatMessage({ defaultMessage: 'Summary is too long or general', id: '3gxcg9' }),
  },
  {
    key: 'inaccurateInfo',
    label: intl.formatMessage({
      defaultMessage: 'Summary included inaccurate information',
      id: 'yEArUx',
    }),
  },
  {
    key: 'missedKeyPoints',
    label: intl.formatMessage({
      defaultMessage: 'Summary failed to capture key points of interest',
      id: 'c2fe7K',
    }),
  },
  {
    key: 'missedEntireSession',
    label: intl.formatMessage({
      defaultMessage: 'Summary failed to capture the entire session',
      id: 'KzwkKD',
    }),
  },
  {
    key: 'other',
    label: intl.formatMessage({ defaultMessage: 'Other', id: '/VnDMl' }),
  },
];

interface AISummaryFeedbackModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** Callback when feedback is submitted */
  onFeedback: (isHelpful: boolean, details: Partial<FeedbackDetails>) => void;
}

export const AISummaryFeedbackModal = ({
  open,
  onClose,
  onFeedback,
}: AISummaryFeedbackModalProps) => {
  const [feedbackForm, setFeedbackForm] = useState<FeedbackDetails>({
    tooLongOrGeneral: false,
    inaccurateInfo: false,
    missedKeyPoints: false,
    missedEntireSession: false,
    other: false,
    additionalFeedback: '',
  });

  const handleCheckboxChange = (key: keyof FeedbackDetails, checked: boolean) => {
    setFeedbackForm(prev => ({ ...prev, [key]: checked }));
  };

  const handleFeedbackSubmit = () => {
    onFeedback(false, feedbackForm);
    onClose();
  };

  return (
    <Modal fullWidth={false} open={open} onCloseSignal={onClose}>
      <Box width={500} padding={24}>
        <Flex column gap={16} alignItems="flex-start">
          <Box width={1}>
            <Flex alignItems="center" gap={8} justifyContent="flex-start">
              <Text appearance="bodyMediumBold">
                <FormattedMessage defaultMessage="What didn't work well?" id="geEghu" />
              </Text>
              <Text appearance="bodyMedium" color="secondary">
                <FormattedMessage defaultMessage="Optional" id="InWqys" />
              </Text>
            </Flex>
            <Flex column alignItems="flex-start">
              {FEEDBACK_OPTIONS.map(option => (
                <Checkbox
                  key={option.key}
                  checked={!!feedbackForm[option.key]}
                  onChange={checked => handleCheckboxChange(option.key, checked)}
                  label={option.label}
                />
              ))}
            </Flex>
          </Box>
          <Box width={1}>
            <Flex alignItems="center" gap={8} justifyContent="flex-start">
              <Text appearance="bodyMediumBold">
                <FormattedMessage defaultMessage="Tell us more" id="gWZSxR" />
              </Text>
              <Text appearance="bodyMedium" color="secondary">
                <FormattedMessage defaultMessage="Optional" id="InWqys" />
              </Text>
            </Flex>
            <TextArea
              resizable
              value={feedbackForm.additionalFeedback}
              onChange={e =>
                setFeedbackForm(prev => ({ ...prev, additionalFeedback: e.target.value }))
              }
              placeholder={intl.formatMessage({
                defaultMessage: 'Please provide any additional feedback...',
                id: 'sEdlqb',
              })}
            />
          </Box>

          <Box width={1}>
            <Flex gap={8} justifyContent="flex-end">
              <Button variant="outline" onClick={onClose}>
                <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
              </Button>
              <Button variant="fill-primary" onClick={handleFeedbackSubmit}>
                <FormattedMessage defaultMessage="Send" id="9WRlF4" />
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </Modal>
  );
};
